/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";
import * as React from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";
import PaymentrecapArrowLeft from "@NA/Icons/PaymentrecapArrowLeft";
const enum fields {
    pageTitle = "pageTitle",
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
    cart: getCurrentCartState(state).value,
    invoicesLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
});

interface CustomProps extends WidgetProps {
    fields: {
        [fields.pageTitle]: string;
    };
}

type Props = WidgetProps & CustomProps & ReturnType<typeof mapStateToProps>;

export interface PageTitleStyles {
    cartLinesLengthContainer?: InjectableCss;
    cartLinesText?: InjectableCss;
    cartLinesLength?: InjectableCss;
    pageTitle?: InjectableCss;
    mainContainer?: InjectableCss;
    button?: InjectableCss;
}

export const pageTitleStyles: PageTitleStyles = {
    cartLinesLengthContainer: {
        css: css`
            margin: 20px 0 10px 10px;
            font-size: 20px;
            line-height: 18px;
            color: ${getColor("text.main")};
            font-family: "Nunito Sans", sans-serif;
            display: inline;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    cartLinesText: {
        css: css`
            font-size: 20px;
            line-height: 18px;
            color: ${getColor("text.main")};
            font-weight: normal;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    cartLinesLength: {
        css: css`
            font-size: 20px;
            line-height: 18px;
            color: ${getColor("text.main")};
            font-weight: normal;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    pageTitle: {
        css: css`
            display: inline-block;
            color: ${ADIColors.darkBlue1};
            font-size: 36px;
            font-weight: 900;
            font-style: normal;
            font-family: "Nunito Sans", sans-serif;
            line-height: 1.11;
            @media print {
                font-size: 36px !important;
                color: ${ADIColors.black};
            }
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    button: {
        css: css`
            background: white !important;
            border: solid 1px #004da6 !important;
            margin-bottom:20px;
            width: auto;
            min-width: 214px;
            border-radius: 4px !important;
            border: solid 1px #004da6;
            min-height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                    min-width: 52px;
                    padding:0 16px;
                    `,
                    css``,
                    null,
                ])}

            > span {
                color: #004da6 !important;                
                font-size: 16px;
                font-weight: bold;
                line-height: normal;
                margin-left:5px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    css`
                        display:block;
                    `,
                    css``,
                    null,
                ])}
                &:before{
                    content: "";
                    position: relative;
                    background-image: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/continue-shop-arrow.svg);
                    background-size: contain;
                    background-position: center center;
                    background-repeat: no-repeat;
                    width: 13px;
                    height: 10px;
                    right:5px;
                    display:inline-block;
                }
            }
        `,
    },
};

const styles = pageTitleStyles;

const ADIPaymentRecapPageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields, invoicesLink }: Props) => {
    const dispatch = useDispatch();
    const callDispatchFunctions = () => {
        dispatch({
            type: "Custom/UpdateRetrunToInvocie",
            result: true,
        });
        dispatch({
            type: "Custom/UpdatePaymentRecap",
            result: false,
        });
    }

    React.useEffect(() => {
        callDispatchFunctions();
    }, []);

    return (
        <StyledWrapper {...styles.mainContainer}>
            <Typography variant="h2" as="h1" data-test-selector="CartPageTitle" {...styles.pageTitle}>
                {translate(fields.pageTitle || pageTitle)}
            </Typography>
            <Clickable
                href={invoicesLink}
                onClick={() => {callDispatchFunctions()}}
            >
                <Button {...styles.button}>{translate("Return to Invoices")}</Button>
            </Clickable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ADIPaymentRecapPageTitle),
    definition: {
        group: "Basic",
        icon: "PageTitle",
        fieldDefinitions: [
            {
                name: fields.pageTitle,
                displayName: "Page Title Overrided",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
